import { Injectable, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { Answer, UpdateResult, PerformByCompletionInfo } from '../models/form-result';
import { AnswerProperty } from '../models/answer';
import { Store } from '@ngrx/store';
import {
  selectAdditionalQuestionsToConvert,
  selectCurrentPage,
  selectTypeName,
  selectVisiblePages
} from '../state/static-form.selectors';
import { take } from 'rxjs';
import { PerformerTypesEnum } from '../models/performed-by';
import { DateFixService } from 'Shared/app/core/helpers/date-fix.service';
import { isIsoDateString } from 'Shared/app/core/helpers/date-format.helper';
import { Languages } from 'Shared/app/constants/languages.constants';

@Injectable()
export class StaticFormDataService {
  readonly #store = inject(Store);
  readonly #dateFixService = inject(DateFixService);

  getAnswers(values: any): Answer[] {
    const answers: Answer[] = values?.questionnaire?.pages?.flatMap((page) => {
      return page.questions?.flatMap((question) => {
        // PerformedBy
        if (question.radios?.find((radio) => radio.hasTextInput)) {
          const performedByAnswers: Answer[] = [
            {
              questionKey: question.id,
              answerKey: question.selectedValue
            }
          ];

          // Find the associated text input and add it to the answers
          for (const radio of question.radios) {
            if (radio.hasTextInput && radio.radio.value === question.selectedValue) {
              performedByAnswers.push({
                questionKey: radio.textInput.id,
                answerKey: radio.textInput.value
              });
              break;
            }
          }
          return performedByAnswers;
        }
        // Radiogrid
        else if (question?.radioGroups?.length > 0) {
          return question.radioGroups.flatMap((rg) => {
            if (rg.selectedValue !== null) {
              return [
                {
                  questionKey: rg.id,
                  answerKey: rg.selectedValue
                }
              ];
            }
            return [];
          });
          // CheckboxGroup
        } else if (question?.checkboxes?.length > 0) {
          return question.checkboxes
            .flatMap((cb) => {
              if (cb.isChecked) {
                return [
                  {
                    questionKey: cb.value,
                    answerKey: 'checked'
                  }
                ];
              }
            })
            .filter((ap) => !!ap);
          // Slider & RadioGroups
        } else if (question?.id !== null && question?.selectedValue !== null && question?.selectedValue !== undefined) {
          return [
            {
              questionKey: question.id,
              answerKey: question.selectedValue.toString()
            }
          ];
          // Text & number inputs
        } else if (question?.id !== null && question?.value !== null && question?.value !== undefined) {
          return [
            {
              questionKey: question.id,
              answerKey: question.value
            }
          ];
        }
        return [];
      });
    });

    return answers;
  }

  getPostDataDiva2(form: UntypedFormGroup, includeDisabledQuestions = false): Answer[] {
    let evaluationQuestions = [];

    this.#store
      .select(selectAdditionalQuestionsToConvert)
      .pipe(take(1))
      .subscribe((questions) => {
        evaluationQuestions = questions;
      });

    const pages: FormArray = (form?.controls.questionnaire as FormGroup).controls.pages as FormArray;

    for (const evaluationQuestion of evaluationQuestions) {
      // Avoid duplicate answers
      if ((pages.at(1) as FormGroup).value.questions.some((q) => q.id === evaluationQuestion.id)) {
        continue;
      }

      ((pages.at(1) as FormGroup).controls.questions as FormArray).push(new FormControl(evaluationQuestion));
    }

    const answers: Answer[] = pages.controls.flatMap((page: FormGroup, pageIndex: number) => {
      let diva2ListQuestionCounter = 0;

      if (page.controls?.pagination?.value === 'EvaluationPage') {
        return [];
      }

      return (page.controls.questions as FormArray).controls.flatMap(
        (questionControl: FormControl, questionIndex: number) => {
          if (!includeDisabledQuestions && questionControl.disabled) return [];

          const question = questionControl.value;
          // Diva 2 ListQuestion
          if (question.checkboxGroup && question.radioGroup && question.textInput) {
            let part = 'Part1';

            if (pageIndex === 6 || pageIndex === 7 || pageIndex === 8) {
              part = 'Part2';
            }

            if (pageIndex === 11 || pageIndex === 12) {
              part = 'CriterionCD';
            }

            // Even number of list questions are Adult, odd are Child
            const type = diva2ListQuestionCounter % 2 === 0 ? 'Adult' : 'Child';

            // Checkboxes
            const listQuestionAnswers: Answer[] = question.checkboxGroup.checkboxes
              .flatMap((cb) => {
                if (cb.isChecked) {
                  return [
                    {
                      questionKey: cb.value,
                      answerKey:
                        cb.customAnswerProperty === null || cb.customAnswerProperty === ''
                          ? 'checked'
                          : cb.customAnswerProperty
                    } satisfies Answer
                  ];
                }
              })
              .filter((ap: any) => !!ap);

            // Radio
            if (question.radioGroup.selectedValue !== null) {
              listQuestionAnswers.push({
                questionKey: question.radioGroup.id,
                answerKey: question.radioGroup.selectedValue,
                part: part,
                type: type
              });
            }

            // Text input
            if (question.checkboxGroup.checkboxes?.at(-1)?.isChecked) {
              listQuestionAnswers.push({
                questionKey: question.textInput.id,
                answerKey: question.textInput.value
              });
            }

            diva2ListQuestionCounter++;
            return listQuestionAnswers;
          }

          return (
            this.convertDoubleRadioGridDiva2(question, questionIndex) ??
            this.convertTextInputRadioGroup(question) ??
            this.convertRadiogrid(question) ??
            this.convertCheckboxGroup(question) ??
            this.convertSliderOrRadioGroup(question) ??
            this.convertTextOrNumberInput(question) ??
            []
          );
        }
      );
    });

    let visiblePagesLength = 0;

    this.#store
      .select(selectVisiblePages)
      .pipe(take(1))
      .subscribe((pages) => (visiblePagesLength = pages.length));

    if (visiblePagesLength > 3) {
      answers.push({
        questionKey: 'form',
        answerKey: 'long'
      });
    } else {
      answers.push({
        questionKey: 'form',
        answerKey: 'short'
      });
    }

    let currentPageIndex = 0;

    this.#store
      .select(selectCurrentPage)
      .pipe(take(1))
      .subscribe((pageIndex) => (currentPageIndex = pageIndex));

    answers.push({
      questionKey: 'currentPageIndex',
      answerKey: currentPageIndex.toString()
    });

    return answers.filter((ap) => !!ap);
  }

  /** Get answer data from form */
  getPostData(form: UntypedFormGroup, includeDisabledQuestions = false): Answer[] {
    let formTypeName = '';
    this.#store
      .select(selectTypeName)
      .pipe(take(1))
      .subscribe((typeName) => (formTypeName = typeName));

    if (formTypeName === 'DIVA2') {
      return this.getPostDataDiva2(form, includeDisabledQuestions);
    }

    const questionnaire = form?.controls?.questionnaire as FormGroup;
    if (!questionnaire) return [];

    const pages = questionnaire.controls?.pages as FormArray;
    if (!pages) return [];

    const answers: Answer[] = pages.controls.flatMap((page: FormGroup) => {
      const questions = page.controls?.questions as FormArray;
      if (!questions) return [];

      return questions.controls
        .filter(x => (includeDisabledQuestions ? true : x.enabled))
        .flatMap((questionControl: FormControl) => {
          const question = questionControl.value;
          return this.convertQuestionValue(question);
        }) ?? [];
    });

    return answers.filter(ap => !!ap);
  }

  convertQuestionValue(questionValue): Answer[] {
    return (
      this.convertPerformedBy(questionValue) ??
      this.convertDropdown(questionValue) ??
      this.convertDoubleRadioGrid(questionValue) ??
      this.convertTable(questionValue) ??
      this.convertRadiogrid(questionValue) ??
      this.convertCheckboxGroup(questionValue) ??
      this.convertSliderOrRadioGroup(questionValue) ??
      this.convertCheckboxRow(questionValue) ??
      this.convertTextOrNumberInput(questionValue) ??
      []
    );
  }

  /** Get form data for update */
  getUpdateData(form: UntypedFormGroup, formId: string, languageId: number, performedAt: Date): UpdateResult {
    const answers = this.getPostData(form);

    const update = {
      answers: answers,
      formId: formId,
      performedAt: this.#dateFixService.getDateString(performedAt),
      languageId: languageId,
      performByCompletionInfo: this.getPerformByCompletionInfo(answers)
    } satisfies UpdateResult;

    return update;
  }

  getPerformByCompletionInfo(answers: Answer[]): PerformByCompletionInfo | null {
    const roleAnswerKey = answers.find((a) => a.questionKey === 'role')?.answerKey ?? null;
    const performerTypeName = answers.find((a) => a.questionKey === 'role.text')?.answerKey ?? null;
    const performerTypeOther = answers.find((a) => a.questionKey === 'role.textother')?.answerKey ?? null;

    if (!roleAnswerKey) {
      return null;
    }

    const performerType = this.#toPerformerType(roleAnswerKey);
    if (performerType === null) {
      console.error(`Unknown 'role' answer '${roleAnswerKey}', performer information was left out of the result.`);
      return null;
    }

    return {
      performerType: performerType,
      performerTypeName: performerTypeName,
      performerTypeOther: performerTypeOther
    } satisfies PerformByCompletionInfo;
  }

  /**
   * Resolves the `role` answer to a PerformerTypesEnum value.
   *
   * Most forms store the numeric enum value as the answer key, but 5-15 rewrites its performedBy
   * radios to semantic labels (see five-fifteen.component.ts), so both shapes turn up here. The
   * backend has no string enum converter registered and only accepts the numeric value, so the
   * raw answer key must never be passed through as-is.
   */
  #toPerformerType(roleAnswerKey: string): PerformerTypesEnum | null {
    const performerTypeByLabel: Record<string, PerformerTypesEnum> = {
      parent: PerformerTypesEnum.Parent,
      teacher: PerformerTypesEnum.Teacher,
      other: PerformerTypesEnum.OtherGuardian
    };

    const labelled = performerTypeByLabel[roleAnswerKey];
    if (labelled !== undefined) {
      return labelled;
    }

    const numeric = Number(roleAnswerKey);
    return Number.isInteger(numeric) && PerformerTypesEnum[numeric] !== undefined ? numeric : null;
  }

  /** Get answer value by answer property key */
  getAnswerValue(key: string, answerProperties: AnswerProperty[]) {
    return answerProperties.find((ap) => ap.key === key)?.value ?? '';
  }

  /** Get answer values by answer property keys in array */
  getAnswerValues(keys: string[], answerProperties: AnswerProperty[]) {
    return keys.map((key) => this.getAnswerValue(key, answerProperties));
  }

  convertTextInputRadioGroup(question: any) {
    if (question.radioGroup && question.textInput) {
      const answers: Answer[] = [];
      if (question.radioGroup.selectedValue !== null) {
        answers.push({
          questionKey: question.radioGroup.id,
          answerKey: question.radioGroup.selectedValue
        });
      }

      if (question.textInput.value !== null && question.textInput.value !== '') {
        answers.push({
          questionKey: question.textInput.id,
          answerKey: question.textInput.value
        });
      }
      return answers;
    }

    return null;
  }

  convertPerformedBy(question: any) {
    if (question.radios?.find((radio) => radio.hasTextInput)) {
      if (question.selectedValue !== null) {
        const performedByAnswers: Answer[] = [
          {
            questionKey: question.id,
            answerKey: question.selectedValue
          }
        ];

        // Find the associated text input and add it to the answers
        for (const radio of question.radios) {
          if (radio.hasTextInput && radio.radio.value === question.selectedValue) {
            performedByAnswers.push({
              questionKey: radio.textInput.id,
              answerKey: radio.textInput.value
            });
            break;
          }
        }
        return performedByAnswers;
      }
    }

    return null;
  }

  convertDropdown(question: any) {
    if (question.dropdownValues?.length > 0) {
      return [
        {
          questionKey: question.id,
          answerKey: question.selectedValue.toString()
        }
      ];
    }
    return null;
  }

  convertDoubleRadioGrid(question: any) {
    if (question.rows?.length > 0 && question.rows[0].radioGroup1) {
      return question.rows.flatMap((row) => {
        const radioGroupAnswers: Answer[] = [];

        if (row.radioGroup1.selectedValue !== null && row.radioGroup1.selectedValue !== '') {
          radioGroupAnswers.push({
            questionKey: row.radioGroup1.id,
            answerKey: row.radioGroup1.selectedValue
          });
        }

        if (row.radioGroup2.selectedValue !== null && row.radioGroup2.selectedValue !== '') {
          radioGroupAnswers.push({
            questionKey: row.radioGroup2.id,
            answerKey: row.radioGroup2.selectedValue
          });
        }

        return radioGroupAnswers;
      });
    }

    return null;
  }

  convertDoubleRadioGridDiva2(question: any, questionIndex: number) {
    if (question.rows?.length > 0 && question.rows[0].radioGroup1) {
      return question.rows.flatMap((row) => {
        const radioGroupAnswers: Answer[] = [];

        if (row.radioGroup1.selectedValue !== null && row.radioGroup1.selectedValue !== '') {
          radioGroupAnswers.push({
            questionKey: row.radioGroup1.id,
            answerKey: row.radioGroup1.selectedValue,
            type: 'Child',
            part: questionIndex === 1 ? 'Part1' : 'Part2'
          });
        }

        if (row.radioGroup2.selectedValue !== null && row.radioGroup2.selectedValue !== '') {
          radioGroupAnswers.push({
            questionKey: row.radioGroup2.id,
            answerKey: row.radioGroup2.selectedValue,
            type: 'Adult',
            part: questionIndex === 1 ? 'Part1' : 'Part2'
          });
        }

        return radioGroupAnswers;
      });
    }

    return null;
  }

  convertTable(question: any) {
    if (question.rows && Array.isArray(question.rows)) {
      const tableAnswers = [];
      if (question.rows?.length > 0) {
        for (let rowIndex = 1; rowIndex <= question.rows?.length; rowIndex++) {
          for (const q of question.rows[rowIndex - 1].questions) {
            if (q.value?.length > 0) {
              tableAnswers.push({
                questionKey: `row.${rowIndex}.${q.id}`,
                answerKey: q.value
              });
            }
          }
        }
        if (tableAnswers.length > 0) {
          tableAnswers.push({
            questionKey: question.id + '.tablerows',
            answerKey: question.rows?.length.toString()
          });
        }
        return tableAnswers;
      }
    }

    return null;
  }

  convertRadiogrid(question: any) {
    if (question?.radioGroups?.length > 0) {
      return question.radioGroups.flatMap((rg) => {
        if (rg.selectedValue !== null) {
          return [
            {
              questionKey: rg.id,
              answerKey: rg.selectedValue
            }
          ];
        }
        return [];
      });
    }

    return null;
  }

  convertCheckboxGroup(question: any) {
    if (question?.checkboxes?.length > 0) {
      return question.checkboxes
        .flatMap((cb) => {
          if (cb.isChecked) {
            return [
              {
                questionKey: cb.value,
                answerKey:
                  cb.customAnswerProperty === null || cb.customAnswerProperty === ''
                    ? 'checked'
                    : cb.customAnswerProperty
              }
            ];
          }
        })
        .filter((ap) => !!ap);
    }

    return null;
  }

  convertCheckboxRow(question: any) {
    if (question?.checkbox && question?.checkbox?.isChecked) {
      return [
        {
          questionKey: question.id,
          answerKey: 'true'
        }
      ];
    }

    return null;
  }

  convertSliderOrRadioGroup(question: any) {
    if (question?.id !== null && question?.selectedValue !== null && question?.selectedValue !== undefined) {
      return [
        {
          questionKey: question.id,
          answerKey: question.selectedValue.toString()
        }
      ];
    }

    return null;
  }

  /**
   * Also converts dates
   */
  convertTextOrNumberInput(question: any) {
    if (question?.id !== null && question?.value !== null && question?.value !== undefined && question?.value !== '') {
      let value = '';

      if (typeof question.value === 'object' && question.value instanceof Date) {
        value = (question.value as Date).toLocaleDateString(Languages.Swedish.resource);
      } else if (typeof question.value === 'string' && isIsoDateString(question.value)) {
        value = new Date(question.value).toLocaleDateString(Languages.Swedish.resource);
      } else {
        value = (typeof question.value === 'number' ? question.value.toString() : question.value).trim();
      }

      return [
        {
          questionKey: question.id,
          answerKey: value
        }
      ];
    }

    return null;
  }
}